// Single source of truth for deal attribution. Two separate axes, and they
// must never be collapsed into one:
//
//   UNITS + VOLUME -> exactly ONE agent per deal (creditedAgentId).
//     A transaction and its sale price belong to one person. Two people can
//     work a deal, but the team closed one deal, not two, so anything that
//     counts deals or adds up volume must ask creditedAgentId / dealCreditsAgent.
//
//   DOLLARS -> EVERY person actually paid on the deal (commissionForAgent).
//     A deal can pay the agent and the team lead. Each of them sees their own
//     slice, and the slices add back up to what the deal paid out. The person
//     who gets no unit still gets his money.
//
// Who owns the unit:
//   - In-house referral -> the team lead of record.
//   - Anything else     -> the agent on the deal.
//   - No agent set      -> fall back to the team lead.
//
// "In-house referral" REQUIRES splittype === "TeamSplit". The In-House Agent
// Referral toggle only exists in the UI while the split type is TeamSplit
// (src/components/home/add-deal.jsx ~line 1786), and switching the split type
// away does not clear the flag, so deals carry stale inhousereferral:true
// values that must not divert the unit away from the agent who earned it.

// agentData is normally a plain id string, but older deals stored the whole
// agent object, so every id read goes through here before it is compared.
function normalizeId(value) {
  if (typeof value === "object" && value !== null) {
    return value.id ? String(value.id) : "";
  }
  return value || value === 0 ? String(value) : "";
}

// splittype is free-form text written by several different screens and by the
// bulk importer, so the stored value drifts ("teamsplit", "TeamSplit ").
// Comparing it raw meant a stray space or a lowercase letter silently moved a
// deal's unit from the team lead to the agent. Fold it before comparing.
function isTeamSplit(deal) {
  return String(deal?.splittype || "").trim().toLowerCase() === "teamsplit";
}

// The one agent who owns this deal's transaction unit and its sale volume.
// Returns "" when the deal names nobody.
export function creditedAgentId(deal) {
  if (!deal) return "";
  const agent = normalizeId(deal.agentData);
  const teamLead = normalizeId(deal.assignedteamlead);
  const isInhouseReferral = !!deal.inhousereferral && isTeamSplit(deal);
  if (isInhouseReferral && teamLead) return teamLead;
  if (agent) return agent;
  return teamLead;
}

// Does this agent own the unit and volume for this deal? Use this everywhere
// deals are counted or volume is summed, so a deal is never counted twice.
export function dealCreditsAgent(deal, agentId) {
  if (!agentId && agentId !== 0) return false;
  return creditedAgentId(deal) === String(agentId);
}

// The dollars this agent personally earned on this deal. Additive on purpose:
// one person can be both the agent and the team lead on the same deal, and the
// referring agent is paid his fee even though he is not credited with the unit.
export function commissionForAgent(deal, agentId) {
  if (!deal) return 0;
  if (!agentId && agentId !== 0) return 0;
  const wanted = String(agentId);
  let total = 0;
  if (normalizeId(deal.agentData) === wanted) {
    total += Number(deal.agentCommissionamount) || 0;
  }
  if (normalizeId(deal.assignedteamlead) === wanted) {
    total += Number(deal.teamleadCommissionamount) || 0;
  }
  return total;
}

// Every dollar the deal recorded for somebody OTHER than this person. Used only
// by the legacy fallback below, so one person can never be handed money the
// deal already recorded as belonging to the other party.
function otherSlicesTotal(deal, agentId) {
  const wanted = String(agentId);
  let sum = 0;
  if (normalizeId(deal.agentData) !== wanted) {
    sum += Number(deal.agentCommissionamount) || 0;
  }
  if (normalizeId(deal.assignedteamlead) !== wanted) {
    sum += Number(deal.teamleadCommissionamount) || 0;
  }
  return sum;
}

// Was a per-person slice actually WRITTEN on this deal? A recorded 0 counts as
// written: zero is a real statement that this person was paid nothing on this
// sale, not a gap in the record. Missing, blank, and the old importer's junk
// ("--", which reads as NaN) are gaps, and only a gap can be filled in from the
// deal's gross.
function sliceRecorded(value) {
  if (value === null || value === undefined || value === "") return false;
  return Number.isFinite(Number(value));
}

// A deal whose agent slice AND team-lead slice were both written down has a
// complete breakdown. Nothing about it needs guessing, so the gross fallback
// below must keep its hands off it entirely.
function breakdownIsComplete(deal) {
  return (
    sliceRecorded(deal.agentCommissionamount) &&
    sliceRecorded(deal.teamleadCommissionamount)
  );
}

// The income figure the two PRINTED coaching sheets show for one deal:
// Know Your Numbers and the IMPACT Scorecard both call this, so they can never
// drift apart again.
//
//   No agentId (the "My Team" view) -> the deal's gross commission, counted
//     once, falling back to the slices the deal DID record when no gross was
//     ever stored.
//   A person -> the dollars he was actually paid (commissionForAgent).
//
// The team-view fallback adds BOTH recorded slices, not just the agent's. It
// used to read the agent slice alone, so an old deal with no stored gross made
// the My Team line smaller than the individual sheets it is supposed to be the
// sum of: the team lead's share simply went missing between the two printouts.
//
// The per-person fallback exists for genuinely old deals that recorded a gross
// commission and no per-person breakdown at all. It hands that gross to the ONE
// agent credited with the deal, minus anything the deal already recorded for the
// other party. That subtraction is the whole point: without it, a sale that
// paid the referring agent $2,000 and stored nothing for the team lead printed
// the full $9,666 gross on the team lead's sheet AND $2,000 on the referrer's,
// billing $11,666 of a $9,666 sale. Per-person incomes now always add back up
// to what the deal actually paid out, and never to more.
//
// The fallback is also skipped outright once a deal has BOTH slices written
// down. A deal recording $0 to the agent and $0 to the team lead against a real
// gross is not a gap waiting to be filled: it is a breakdown that says nobody
// was paid, and it must print as nothing on both sheets. Before this guard the
// subtraction had nothing to subtract, so the credited agent was handed the
// entire gross as personal income he never received.
export function incomeForAgent(deal, agentId) {
  if (!deal) return 0;
  const total = Number(deal.totalCommissionamount) || 0;
  if (!agentId && agentId !== 0) {
    if (total) return total;
    return (
      (Number(deal.agentCommissionamount) || 0) +
      (Number(deal.teamleadCommissionamount) || 0)
    );
  }
  const paid = commissionForAgent(deal, agentId);
  if (paid) return paid;
  // Nobody but the credited owner can claim an unbroken-down gross.
  if (!dealCreditsAgent(deal, agentId)) return 0;
  // A complete breakdown is the last word; a recorded zero stays zero.
  if (breakdownIsComplete(deal)) return 0;
  const leftover = total - otherSlicesTotal(deal, agentId);
  return leftover > 0 ? leftover : 0;
}

// Cancelled and expired deals shouldn't show up in deal-count, volume, or
// commission totals — they're dead. Use this anywhere you sum or count deals
// for performance metrics.
export function isCountableDeal(deal) {
  const stage = (deal?.dealStage || "").toLowerCase();
  return stage !== "cancelled" && stage !== "expired";
}

// Stage buckets for reports. "Closed" is banked money; "pending" is a live
// deal still in flight (any stage that isn't closed or dead). Reports that
// group by year/month must ALSO require deal.closingdate — a pending deal
// with no closing date can't be placed on a calendar.
export function isClosedDeal(deal) {
  return (deal?.dealStage || "").toLowerCase() === "closed";
}

export function isPendingDeal(deal) {
  return !isClosedDeal(deal) && isCountableDeal(deal);
}
